import React, { useState, useRef, useEffect } from 'react';
import SmallButton from './SmallButton';
import MediumButton from './MediumButton';
import typography from '../../utils/typography';
import colors from '../../utils/colors';
import { CloseIcon, PlayIcon, ThumbsUpIcon, CommentIcon, ShareIcon, GlobeIcon } from '../../utils/icons';

// Helper function to format like counts
const formatLikeCount = (count) => {
  if (count >= 1000) {
    const thousands = count / 1000;
    // If it's a whole number (like 1000, 2000), show as "1k", "2k"
    if (thousands % 1 === 0) {
      return thousands + 'k';
    }
    // Otherwise show one decimal place (like 1.1k, 2.1k)
    return thousands.toFixed(1) + 'k';
  }
  return count.toString();
};

const SidePanel = ({ isOpen, onClose, song, isWorkspace = false, isLiked = false, onLike, hasPlaybar = false, onTitleEdit }) => {
  const [stylesExpanded, setStylesExpanded] = useState(false);
  const [isEditingTitle, setIsEditingTitle] = useState(false);
  const [editTitle, setEditTitle] = useState(song?.title || "");
  const [isTitleHovered, setIsTitleHovered] = useState(false);
  const titleInputRef = useRef(null);

  // Update editTitle when song changes
  useEffect(() => {
    setEditTitle(song?.title || "");
  }, [song?.title]);

  // Focus input when editing starts
  useEffect(() => {
    if (isEditingTitle && titleInputRef.current) {
      titleInputRef.current.focus();
      titleInputRef.current.select();
    }
  }, [isEditingTitle]);

  const handleTitleEditStart = () => {
    setIsEditingTitle(true);
  };

  const handleTitleEditSave = () => {
    if (editTitle.trim() && editTitle !== song.title) {
      onTitleEdit?.(song.id, editTitle.trim());
    }
    setIsEditingTitle(false);
  };

  const handleTitleEditCancel = () => {
    setEditTitle(song?.title || "");
    setIsEditingTitle(false);
  };

  const handleTitleKeyDown = (e) => {
    if (e.key === 'Enter') {
      handleTitleEditSave();
    } else if (e.key === 'Escape') {
      handleTitleEditCancel();
    }
  };

  const handleTitleBlur = () => {
    handleTitleEditSave();
  };
  
  if (!isOpen || !song) return null;

  return (
    <div
      className="absolute top-3 right-3 z-50 w-[260px] rounded-[20px] border border-white/10"
      style={{
        backgroundColor: colors.bg.primary,
        height: hasPlaybar ? 'calc(100% - 104px)' : 'calc(100% - 24px)',
        boxShadow: '0px 0px 20px 20px rgba(0,0,0,0.4)'
      }}
    >
      {/* Close Button */}
      <div className="absolute top-3 right-3 z-10">
        <SmallButton
          variant="tertiary"
          onClick={onClose}
          leftIcon={<CloseIcon />}
          tooltip="Close"
        />
      </div>

      {/* Scrollable Content Area */}
      <div className="overflow-y-auto h-full px-3" style={{ paddingTop: '16px' }}>
        {/* Artwork Section */}
        <div className="w-full flex justify-center">
          <div className="w-[160px] h-[200px] relative">
            {/* Background artwork (blurred) */}
            <img
              className="absolute inset-0 w-full h-full opacity-30 rounded-[20px] object-cover"
              style={{ filter: 'blur(15px)' }}
              src={song.image?.replace(/w=\d+&h=\d+/, 'w=320&h=400') || "https://images.unsplash.com/photo-1493225457124-a3eb161ffa5f?w=320&h=400&fit=crop&crop=center"}
              alt="Background artwork"
            />
            {/* Main artwork (centered) */}
            <img
              className="absolute left-1/2 top-1/2 transform -translate-x-1/2 -translate-y-1/2 w-[120px] h-[150px] rounded-[12px] object-cover shadow-lg"
              src={song.image?.replace(/w=\d+&h=\d+/, 'w=240&h=300') || "https://images.unsplash.com/photo-1493225457124-a3eb161ffa5f?w=240&h=300&fit=crop&crop=center"}
              alt="Song artwork"
            />
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex items-center w-full gap-2 -mt-1">
        {/* Play Button */}
        <div className="flex-1">
          <SmallButton
            variant="secondary"
            leftIcon={<PlayIcon />}
            label="7"
            tooltip="Play"
          />
        </div>

        {/* Like Button */}
        <div className="flex-1">
          <SmallButton
            variant={isLiked ? "primary" : "secondary"}
            leftIcon={<ThumbsUpIcon />}
            label={song.likeCount ? formatLikeCount(song.likeCount) : '0'}
            onClick={onLike}
            tooltip="Like"
          />
        </div>

        {/* Comments Button */}
        <div className="flex-1">
          <SmallButton
            variant="secondary"
            leftIcon={<CommentIcon />}
            label={song.commentCount ? formatLikeCount(song.commentCount) : '0'}
            tooltip="Comments"
          />
        </div>

        {/* Share Button */}
        <div className="flex-1">
          <SmallButton
            variant="secondary"
            leftIcon={<ShareIcon />}
            tooltip="Share"
          />
        </div>
        </div>

        {/* Song Info - 16px from action buttons */}
        <div className="flex flex-col w-full gap-4 mt-4">
          {/* Title and Description */}
          <div className="flex flex-col">
            <div className="flex flex-col gap-1">
              <div 
                className="relative group"
                onMouseEnter={() => setIsTitleHovered(true)}
                onMouseLeave={() => setIsTitleHovered(false)}
              >
                {isEditingTitle ? (
                  <input
                    ref={titleInputRef}
                    type="text"
                    value={editTitle}
                    onChange={(e) => setEditTitle(e.target.value)}
                    onKeyDown={handleTitleKeyDown}
                    onBlur={handleTitleBlur}
                    className="text-left bg-transparent border-b border-white/30 focus:border-white/60 focus:outline-none w-full pb-1"
                    style={{ ...typography.medium.title, color: '#FAF7F5' }}
                    placeholder="Song title"
                  />
                ) : (
                  <div className="flex items-center gap-2">
                    <h2 className="text-left flex-1" style={{ ...typography.medium.title, color: '#FAF7F5' }}>
                      {song.title || "aura is coming"}
                    </h2>
                    {isTitleHovered && (
                      <button
                        onClick={handleTitleEditStart}
                        className="p-1 rounded hover:bg-white/10 transition-colors flex-shrink-0"
                        title="Edit title"
                      >
                        <svg width={14} height={14} viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path d="M2.66667 14C2.29848 14 2 13.7015 2 13.3333V11.4432C2 11.2661 2.07041 11.0964 2.19571 10.9713L10.8 2.38333C10.9333 2.26111 11.0806 2.16667 11.2417 2.1C11.4028 2.03333 11.5722 2 11.75 2C11.9278 2 12.1 2.03333 12.2667 2.1C12.4333 2.16667 12.5778 2.26667 12.7 2.4L13.6167 3.33333C13.75 3.45556 13.8472 3.6 13.9083 3.76667C13.9694 3.93333 14 4.1 14 4.26667C14 4.44444 13.9694 4.61389 13.9083 4.775C13.8472 4.93611 13.75 5.08333 13.6167 5.21667L5.0286 13.8047C4.90357 13.9298 4.734 14 4.55719 14H2.66667ZM11.7333 5.2L12.6667 4.26667L11.7333 3.33333L10.8 4.26667L11.7333 5.2Z" fill="#FAF7F5"/>
                        </svg>
                      </button>
                    )}
                  </div>
                )}
              </div>
            </div>
            <div className="flex items-start">
              <p className="text-left" style={{ ...typography.small.regular, color: colors.text.secondary }}>worked really hard on this 💎</p>
            </div>
          </div>

        {/* Styles Section */}
        <div className="relative flex flex-col gap-3 p-3 rounded-xl" style={{ backgroundColor: colors.bg.tertiary }}>
          <div className="pr-8">
            <div className="w-full">
              <p className={`mb-1 text-left ${stylesExpanded ? '' : 'truncate'}`} style={{ ...typography.xsmall.regular, color: colors.text.tertiary }}>
                chill uk garage, swingy unquantized drums, soft female vocals
              </p>
              {stylesExpanded ? (
                <div className="space-y-1">
                  <p className="text-left" style={typography.xsmall.regular}>
                    <span style={{ color: colors.text.primary, fontWeight: '500' }}>Weirdness </span>
                    <span style={{ color: colors.text.secondary }}>88%</span>
                  </p>
                  <p className="text-left" style={typography.xsmall.regular}>
                    <span style={{ color: colors.text.primary, fontWeight: '500' }}>Style Influence </span>
                    <span style={{ color: colors.text.secondary }}>71%</span>
                  </p>
                  <p className="text-left" style={typography.xsmall.regular}>
                    <span style={{ color: colors.text.primary, fontWeight: '500' }}>Audio Influence </span>
                    <span style={{ color: colors.text.secondary }}>93%</span>
                  </p>
                  <p className="text-left" style={typography.xsmall.regular}>
                    <span style={{ color: colors.text.primary, fontWeight: '500' }}>Vocal Gender </span>
                    <span style={{ color: colors.text.secondary }}>Female</span>
                  </p>
                </div>
              ) : (
                <p className="text-left truncate" style={typography.xsmall.regular}>
                  <span style={{ color: colors.text.primary, fontWeight: '500' }}>Weirdness </span>
                  <span style={{ color: colors.text.secondary }}>88%</span>
                  <span style={{ color: colors.text.tertiary }}> · </span>
                  <span style={{ color: colors.text.primary, fontWeight: '500' }}>Audio Influence </span>
                  <span style={{ color: colors.text.secondary }}>93%</span>
                </p>
              )}
            </div>
          </div>
          {/* Copy Button - Pinned to top-right corner */}
          <div className="absolute top-3 right-3 p-1 cursor-pointer hover:bg-white/10 rounded transition-colors">
            <svg width={16} height={16} viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M3.00004 3.99998C3.36671 3.99998 3.66671 4.29998 3.66671 4.66665V13.3333H10.3334C10.7 13.3333 11 13.6333 11 14C11 14.3666 10.7 14.6666 10.3334 14.6666H3.66671C3.30004 14.6666 2.9866 14.5334 2.7266 14.2734C2.4666 14.0134 2.33337 13.7 2.33337 13.3333V4.66665C2.33337 4.29998 2.63337 3.99998 3.00004 3.99998Z" fill="#FAF7F5"/>
              <path fillRule="evenodd" clipRule="evenodd" d="M12.3334 1.33331C12.7 1.33331 13.0137 1.46413 13.2748 1.72524C13.5359 1.98635 13.6667 2.29998 13.6667 2.66665V10.6666C13.6667 11.0333 13.5359 11.3469 13.2748 11.6081C13.0137 11.8692 12.7 12 12.3334 12H6.33337C5.96671 12 5.65308 11.8692 5.39197 11.6081C5.13086 11.3469 5.00004 11.0333 5.00004 10.6666V2.66665C5.00004 2.29998 5.13086 1.98635 5.39197 1.72524C5.65308 1.46413 5.96671 1.33331 6.33337 1.33331H12.3334ZM6.33337 10.6666H12.3334V2.66665H6.33337V10.6666Z" fill="#FAF7F5"/>
            </svg>
          </div>
          <div className="border-t border-white/10 pt-2">
            <div 
              className="flex justify-center items-center gap-1 cursor-pointer"
              onClick={() => setStylesExpanded(!stylesExpanded)}
            >
              <p style={{ ...typography.xsmall.regular, color: colors.text.primary }}>
                {stylesExpanded ? 'Show less' : 'Show more'}
              </p>
              <svg 
                width={16} 
                height={16} 
                viewBox="0 0 17 16" 
                fill="none" 
                xmlns="http://www.w3.org/2000/svg"
                className={`${stylesExpanded ? 'rotate-180' : ''}`}
              >
                <path d="M8.49653 9.93359L12.7215 5.70861C12.8652 5.5649 13.0353 5.49544 13.2317 5.50023C13.4281 5.50502 13.5981 5.57927 13.7418 5.72298C13.8855 5.86668 13.9574 6.03674 13.9574 6.23314C13.9574 6.42954 13.8855 6.59959 13.7418 6.7433L9.31566 11.1551C9.20069 11.2701 9.07136 11.3563 8.92765 11.4138C8.78394 11.4713 8.64024 11.5 8.49653 11.5C8.35282 11.5 8.20912 11.4713 8.06541 11.4138C7.9217 11.3563 7.79236 11.2701 7.6774 11.1551L3.25122 6.72893C3.10751 6.58522 3.03806 6.41756 3.04285 6.22595C3.04764 6.03434 3.12189 5.86668 3.26559 5.72298C3.4093 5.57927 3.57935 5.50742 3.77575 5.50742C3.97215 5.50742 4.14221 5.57927 4.28591 5.72298L8.49653 9.93359Z" fill="#FAF7F5"/>
              </svg>
            </div>
          </div>
        </div>


          {/* Lyrics */}
          <div className="overflow-y-auto flex-1 text-left mb-16" style={{ color: '#FAF7F5' }}>
            <div className="space-y-4">
              <div>
                <p className="font-medium mb-2 text-left" style={typography.small.regular}>[Verse 1]</p>
                <p className="mb-1 text-left" style={typography.small.regular}>Feel it rising in the dark</p>
                <p className="mb-1 text-left" style={typography.small.regular}>Something golden in the spark</p>
                <p className="mb-1 text-left" style={typography.small.regular}>Whispers through the midnight air</p>
                <p className="mb-4 text-left" style={typography.small.regular}>Like a secret, everywhere</p>
              </div>
              
              <div>
                <p className="font-medium mb-2 text-left" style={typography.small.regular}>[Pre-Chorus]</p>
                <p className="mb-1 text-left" style={typography.small.regular}>All eyes wide, can't look away</p>
                <p className="mb-1 text-left" style={typography.small.regular}>Light bends where she moves, they say</p>
                <p className="mb-1 text-left" style={typography.small.regular}>It's not luck, it's not a warning</p>
                <p className="mb-4 text-left" style={typography.small.regular}>You just know… aura's forming</p>
              </div>
              
              <div>
                <p className="font-medium mb-2 text-left" style={typography.small.regular}>[Chorus]</p>
                <p className="mb-1 text-left" style={typography.small.regular}>Aura is coming, better hold tight</p>
                <p className="mb-1 text-left" style={typography.small.regular}>Shifting the room with a blink of light</p>
                <p className="mb-1 text-left" style={typography.small.regular}>Not just a vibe, it's a force divine</p>
                <p className="mb-4 text-left" style={typography.small.regular}>Aura is coming — crossing the line</p>
              </div>
              
              <div>
                <p className="font-medium mb-2 text-left" style={typography.small.regular}>[Verse 2]</p>
                <p className="mb-1 text-left" style={typography.small.regular}>Barefoot steps on mirrored floors</p>
                <p className="mb-1 text-left" style={typography.small.regular}>Unlocking all the unseen doors</p>
                <p className="mb-1 text-left" style={typography.small.regular}>Every shade begins to glow</p>
                <p className="text-left" style={typography.small.regular}>When she's near, the silence knows</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Bottom Make Public Button - Anchored to bottom */}
      <div className="absolute bottom-0 left-0 right-0 p-3 border-t border-white/10 rounded-b-[20px]" style={{ backgroundColor: colors.bg.primary }}>
        <MediumButton
          variant="secondary"
          text="Publish"
          leftIcon={<GlobeIcon />}
          className="w-full"
        />
      </div>
    </div>
  );
};

export default SidePanel;